/**
 * NF525 banner — reminds the user to perform the daily Z closure
 * when > 24h have passed since the last one AND there are sales to close.
 *
 * Compact by default. Non-blocking (user can still work) but visually
 * red + persistent until the Z is run. The CTA triggers perform_z_closure
 * which is a catch-up aggregation (fiscally valid).
 */
import { useState } from 'react';
import { AlertTriangle, Receipt, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useZClosureReminder } from '@/hooks/useZClosureReminder';
import { triggerManualZClosure } from '@/hooks/useFiscal';
import { useAuth } from '@/contexts/AuthContext';
import { useEffectiveTenantId } from '@/hooks/useTenant';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

// Per-tenant localStorage dismiss key (resets each day so the reminder comes
// back tomorrow — just not right now after the user acknowledged it).
function dismissKey(tenantId: string | null): string {
  const d = new Date();
  const today = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return `vp_z_banner_dismissed_${tenantId || 'no-tenant'}_${today}`;
}

export default function ZClosureBanner({ compact = false }: { compact?: boolean }) {
  const { data: status } = useZClosureReminder();
  const { user } = useAuth();
  const tenantId = useEffectiveTenantId();
  const queryClient = useQueryClient();
  const [running, setRunning] = useState(false);
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return !!localStorage.getItem(dismissKey(tenantId));
    } catch {
      return false;
    }
  });

  if (!status?.isOverdue || dismissed) return null;

  const handleDismiss = () => {
    try {
      localStorage.setItem(dismissKey(tenantId), '1');
    } catch {
      /* ignore */
    }
    setDismissed(true);
  };

  const handleRunZ = async () => {
    if (!user) return;
    setRunning(true);
    try {
      const result = await triggerManualZClosure(user.id);
      toast.success(`Clôture Z n°${(result as any)?.closure_number ?? '?'} effectuée`);
      queryClient.invalidateQueries({ queryKey: ['z_closures'] });
      queryClient.invalidateQueries({ queryKey: ['fiscal_grand_totals'] });
      queryClient.invalidateQueries({ queryKey: ['z_closure_reminder'] });
    } catch (err: any) {
      toast.error(`Erreur clôture Z : ${err?.message || 'inconnue'}`);
    } finally {
      setRunning(false);
    }
  };

  // Visual severity follows legal urgency, not scare tactics:
  // - 1 day late: amber reminder (informational, NF525 obligation recalled)
  // - 3+ days late: red, since the JET has drifted meaningfully
  // Always non-blocking — the user can still work and run the Z later.
  const severity = status.overdueDays >= 3 ? 'red' : 'amber';
  const label =
    status.overdueDays >= 2
      ? `${status.overdueDays} jours sans clôture Z`
      : 'Clôture Z journalière à effectuer';

  const detail = `${status.unclosedSalesCount} ticket(s) depuis la dernière clôture — rappel auto-cert (art. 286 CGI)`;

  const tone =
    severity === 'red'
      ? {
          bg: 'bg-red-500/10',
          border: 'border-red-500/30',
          text: 'text-red-700 dark:text-red-400',
          detailText: 'text-red-600/80 dark:text-red-300/80',
          icon: 'text-red-500',
        }
      : {
          bg: 'bg-amber-500/10',
          border: 'border-amber-500/30',
          text: 'text-amber-800 dark:text-amber-300',
          detailText: 'text-amber-700/80 dark:text-amber-300/80',
          icon: 'text-amber-500',
        };

  if (compact) {
    return (
      <div
        className={`flex items-center gap-2 rounded-md ${tone.bg} border ${tone.border} px-3 py-2 text-sm`}
      >
        <AlertTriangle className={`h-4 w-4 ${tone.icon} shrink-0`} />
        <span className={`${tone.text} font-medium`}>{label}</span>
        <Button
          size="sm"
          variant="outline"
          className="ml-auto h-7"
          onClick={handleRunZ}
          disabled={running}
        >
          {running ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <Receipt className="mr-1 h-3 w-3" />
          )}
          {running ? '…' : 'Clôturer'}
        </Button>
        <button
          onClick={handleDismiss}
          className={`${tone.icon} p-0.5 hover:opacity-70`}
          title="Masquer pour aujourd'hui"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  }

  return (
    <div className={`rounded-lg ${tone.bg} border ${tone.border} flex items-start gap-3 p-3`}>
      <AlertTriangle className={`h-5 w-5 ${tone.icon} mt-0.5 shrink-0`} />
      <div className="flex-1">
        <p className={`font-medium ${tone.text}`}>{label}</p>
        <p className={`text-sm ${tone.detailText} mt-0.5`}>{detail}</p>
      </div>
      <Button variant="outline" size="sm" onClick={handleRunZ} disabled={running}>
        {running ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        ) : (
          <Receipt className="mr-2 h-4 w-4" />
        )}
        {running ? 'Clôture en cours…' : 'Faire la clôture Z'}
      </Button>
      <button
        onClick={handleDismiss}
        className={`${tone.icon} p-1 hover:opacity-70`}
        title="Masquer pour aujourd'hui"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
